import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Grid,
  useTheme,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  RadioGroup
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { showNotification, fetchLanguages } from '../../store/actions/uiActions';
import { fetchAtomicProducts } from '../../store/actions/orderAction';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import logger from '../../utils/logger';

// 文件类型图标映射
const fileTypeIcons = {
  'pdf': <PictureAsPdfIcon sx={{ fontSize: 40 }} />,
  'jpg': <ImageIcon sx={{ fontSize: 40 }} />,
  'jpeg': <ImageIcon sx={{ fontSize: 40 }} />,
  'png': <ImageIcon sx={{ fontSize: 40 }} />,
  'doc': <DescriptionIcon sx={{ fontSize: 40 }} />,
  'docx': <DescriptionIcon sx={{ fontSize: 40 }} />,
  'default': <InsertDriveFileOutlinedIcon sx={{ fontSize: 40 }} />
};

// 获取文件扩展名
const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

// 获取文件图标
const getFileIcon = (fileName) => {
  const extension = getFileExtension(fileName);
  return fileTypeIcons[extension] || fileTypeIcons.default;
};

// 截断文件名，保留扩展名
const truncateFileName = (fileName, maxLength = 15) => {
  if (!fileName) return fileName;
  
  // 找到最后一个点的位置
  const lastDotIndex = fileName.lastIndexOf('.');
  
  if (lastDotIndex === -1) {
    // 没有扩展名，直接截断
    return fileName.length > maxLength ? fileName.substring(0, maxLength) + '...' : fileName;
  }
  
  const nameWithoutExt = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex);
  
  // 如果文件名主体超过限制长度，截断并添加省略号
  if (nameWithoutExt.length > maxLength) {
    return nameWithoutExt.substring(0, maxLength) + '...' + extension;
  }
  
  return fileName;
};

// 支持的文件类型
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  //'application/msword': ['.doc'],
  //'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
};

// 文件大小限制 (50MB)
const MAX_FILE_SIZE = 50 * 1024 * 1024;
// 最大文件数
const MAX_FILES = 10;

const TaskUploader = ({
  onComplete,
  isDialog = false,
  onClose = () => {},
  requireAuth = false,
  dropzoneTitle = '拖放签证文件到这里或点击上传',
  dropzoneDescription = '支持JPG、PNG、PDF格式，单个文件最大50MB'
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { languages } = useSelector((state) => state.ui);
  const { atomicProducts } = useSelector((state) => state.order);
  // 获取Redux中的上传状态
  const { loading: isUploading, error: uploadError } = useSelector((state) => state.tasks);
  const [files, setFiles] = useState([]);
  const [language, setLanguage] = useState('en');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [errors, setErrors] = useState([]);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [localUploadError, setLocalUploadError] = useState('');
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  
  // 计算最终的提交状态：本地提交状态或Redux上传状态
  const isSubmitting = localSubmitting || isUploading;
  
  // 防重复提交的最小间隔（毫秒）
  const SUBMIT_COOLDOWN = 2000;

  // 默认语种选项（用于未登录状态）
  const defaultLanguages = [
    { code: 'en', name: '英语' },
    { code: 'zh', name: '中文' }
  ];
  
  // 获取当前可用的语种列表
  const availableLanguages = requireAuth ? defaultLanguages : languages;

  // 加载语种数据
  useEffect(() => {
    // 只有在不需要认证检查时才加载语种数据
    if (!requireAuth) {
      dispatch(fetchLanguages());
    }
  }, [dispatch, requireAuth]);

  // 加载原子商品数据
  useEffect(() => {
    // 获取翻译类原子商品
    dispatch(fetchAtomicProducts({ category: 'translation' }));
  }, [dispatch]);

  // 设置默认选中的原子商品（选择第一个AI翻译商品）
  useEffect(() => {
    if (atomicProducts.data.length > 0 && !selectedProduct) {
      // 优先选择AI翻译商品
      const aiProduct = atomicProducts.data.find(product => 
        product.product_name.includes('AI翻译')
      );
      if (aiProduct) {
        setSelectedProduct(aiProduct.product_id);
      } else {
        // 如果没有AI翻译，选择第一个商品
        setSelectedProduct(atomicProducts.data[0].product_id);
      }
    }
  }, [atomicProducts.data, selectedProduct]);

  // 当语种数据加载完成后，确保当前选中的语种有效
  useEffect(() => {
    if (availableLanguages.length > 0) {
      const isCurrentLanguageValid = availableLanguages.some(lang => lang.code === language);
      if (!isCurrentLanguageValid) {
        // 如果当前语种不在列表中，设置为第一个可用语种
        setLanguage(availableLanguages[0].code);
      }
    }
  }, [availableLanguages, language]);

  // 重置错误信息
  useEffect(() => {
    if (errors.length > 0) {
      const timer = setTimeout(() => {
        setErrors([]);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  // 处理文件拖放和选择
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // 如果需要认证但用户未登录，立即触发认证检查
    if (requireAuth) {
      onComplete(); // 触发登录弹窗
      return;
    }

    // 处理文件类型错误（仍使用内部错误显示）
    const errorMessages = [];
    
    // 检查是否有too-many-files错误，如果有则只显示一次统一提示
    const hasTooManyFiles = rejectedFiles.some(({ errors }) => 
      errors.some(error => error.code === 'too-many-files')
    );
    
    if (hasTooManyFiles) {
      // 文件数量超过限制，使用通知显示统一提示
      dispatch(showNotification(`最多只能上传${MAX_FILES}个文件，超出的文件已被忽略`, 'warning'));
    }
    
    // 处理被拒绝的文件
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach(error => {
        if (error.code === 'file-too-large') {
          // 文件大小超过限制，使用通知
          dispatch(showNotification(`文件 "${file.name}" 太大，超过了50MB限制`, 'warning'));
        } else if (error.code === 'file-invalid-type') {
          // 文件类型不支持的错误保留在表单内部显示
          errorMessages.push(`文件 "${file.name}" 格式不支持`);
        } else if (error.code === 'too-many-files') {
          // too-many-files错误已经在上面统一处理了，这里跳过避免重复显示
          return;
        } else {
          errorMessages.push(`文件 "${file.name}" 上传失败: ${error.message}`);
        }
      });
    });
    
    // 先去除重复文件
    const uniqueFiles = acceptedFiles.filter(newFile => {
      // 检查文件名和大小是否都相同
      return !files.some(existingFile => 
        existingFile.name === newFile.name && 
        existingFile.size === newFile.size
      );
    });
    
    // 检查文件数量限制（使用去重后的文件数量）- 这个检查现在主要是为了处理边界情况
    if (files.length + uniqueFiles.length > MAX_FILES) {
      // 只保留能添加的数量
      const allowedCount = MAX_FILES - files.length;
      uniqueFiles.splice(allowedCount);
      
      // 如果还有文件被截断且前面没有显示过too-many-files提示，则显示提示
      if (!hasTooManyFiles && allowedCount < acceptedFiles.length) {
        dispatch(showNotification(`最多只能上传${MAX_FILES}个文件，超出的文件已被忽略`, 'warning'));
      }
    }
    
    if (errorMessages.length > 0) {
      setErrors(errorMessages);
    }
    
    // 添加新文件
    setFiles(prev => [
      ...prev,
      ...uniqueFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      }))
    ]);
  }, [files, dispatch, requireAuth, onComplete]);

  // 配置拖放区
  const { 
    getRootProps, 
    getInputProps, 
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    // 当需要认证时禁用默认点击行为，我们用自定义处理
    noClick: requireAuth
  });

  // 处理拖放区域点击
  const handleDropzoneClick = (event) => {
    // 如果需要认证但用户未登录，阻止默认行为并触发登录
    if (requireAuth) {
      event.preventDefault();
      event.stopPropagation();
      onComplete(); // 触发登录弹窗
      return;
    }
    // 如果已登录或不需要认证，让默认行为继续（由于noClick=false，这里不需要手动处理）
  };

  // 移除文件
  const removeFile = (index) => {
    setFiles(files => {
      const newFiles = [...files];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // 处理原子商品选择变更
  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
  };

  // 获取认证类型映射
  const getCertificationType = (productName) => {
    if (productName.includes('AI5.0翻译')) {
      return 'none';
    } else if (productName.includes('专业认证')) {
      return 'professional';
    } else if (productName.includes('NAATI')) {
      return 'naati';
    }
    return 'none';
  };
  
  // 加急服务状态
  const [isUrgent, setIsUrgent] = useState(false);
  
  // 获取加急服务信息
  const urgentService = useMemo(() => {
    if (!atomicProducts.data || atomicProducts.data.length === 0) return null;
    return atomicProducts.data.find(product => product.product_name.includes('加急服务'));
  }, [atomicProducts.data]);
  
  // 提交翻译
  const handleSubmit = async () => {
    logger.debug('[FileUploader] 开始提交翻译任务');
    // 获取选中产品的中文名称
    const currentProductInfo = atomicProducts.data.find(
      product => product.product_id === selectedProduct
    );
    const selectedProductName = currentProductInfo?.product_name || selectedProduct;
    
    logger.debug('[FileUploader] 提交前验证:', {
      '文件数量': files.length,
      '翻译类型': selectedProductName,
      '翻译语种': language,
      '正在提交': isSubmitting,
      '本地提交中': localSubmitting,
      '正在上传': isUploading
    });
    
    const currentTime = Date.now();
    
    // 增强的防重复提交检查
    if (isSubmitting) {
      logger.warn('[FileUploader] 提交已在进行中，忽略重复点击');
      return;
    }
    
    // 检查提交间隔，防止快速连续点击
    if (currentTime - lastSubmitTime < SUBMIT_COOLDOWN) {
      logger.warn('[FileUploader] 提交过于频繁，请稍后再试');
      dispatch(showNotification('提交过于频繁，请稍后再试', 'warning'));
      return;
    }
    
    // 记录提交时间
    setLastSubmitTime(currentTime);
    
    if (files.length === 0) {
      logger.warn('[FileUploader] 提交失败: 没有上传文件');
      dispatch(showNotification('请至少上传一个文件', 'warning'));
      return;
    }
    
    if (!selectedProduct) {
      logger.warn('[FileUploader] 提交失败: 未选择翻译服务类型');
      dispatch(showNotification('请选择翻译服务类型', 'warning'));
      return;
    }
    
    if (!language) {
      logger.warn('[FileUploader] 提交失败: 未选择语种');
      dispatch(showNotification('请选择语种', 'warning'));
      return;
    }
    
    logger.info('[FileUploader] 验证通过，开始处理提交');
    setLocalSubmitting(true);
    
    // 如果需要认证但用户未登录，触发认证检查
    if (requireAuth) {
      logger.info('[FileUploader] 需要认证，触发登录弹窗');
      onComplete(); // 触发登录弹窗
      // 重置本地提交状态
      setLocalSubmitting(false);
      return;
    }
    
    // 获取选中的商品信息和认证类型
    const selectedProductInfo = atomicProducts.data.find(
      product => product.product_id === selectedProduct
    );
    
    const certificationType = getCertificationType(selectedProductInfo?.product_name || '');
    
    logger.debug('[FileUploader] 提交参数详情:', {
      '文件数量': atomicProducts.data.length,
      '翻译类型': selectedProductInfo?.product_name || selectedProduct,
      '认证类型': certificationType,
      '翻译语种': language,
      '是否加急': isUrgent,
      '文件信息': files.map(file => ({
        '文件名': file.name,
        '文件大小': file.size,
        '文件类型': file.type
      }))
    });
    
    logger.debug('[FileUploader] 调用onComplete回调，开始文件上传');
    
    try {
      // 清除之前的错误状态
      setErrors([]);
      setLocalUploadError('');
      
      // 清除Redux中的错误状态（如果存在）
      if (uploadError) {
        dispatch({ type: 'CLEAR_UPLOAD_ERROR' });
      }
      
      // 调用onComplete回调，传递files、language、certification_type和is_urgent
      await onComplete(files, language, certificationType, isUrgent);
      logger.debug('[FileUploader] 文件上传中');
    } catch (error) {
      logger.error('[FileUploader] 文件上传失败:', error);
      
      // 设置本地错误信息
      const errorMessage = error.message || '上传失败，请重试';
      setLocalUploadError(errorMessage);
      
      // 显示错误通知（避免重复通知）
      if (!error.handled) {
        dispatch(showNotification(errorMessage, 'error'));
      }
      
      // 重置提交时间戳，允许立即重试
      setLastSubmitTime(0);
    } finally {
      // 无论成功还是失败，都重置本地提交状态
      logger.debug('[FileUploader] 重置本地提交状态');
      setLocalSubmitting(false);
    }
  };

  // 拖放区样式
  const dropzoneStyle = {
    borderRadius: '40px',
    border: `1px dashed ${isDragReject ? theme.palette.error.main : isDragAccept ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: isDragActive ? 'rgba(67, 83, 255, 0.08)' : '#FFFFFF',
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '150px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(67, 83, 255, 0.05)'
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      minHeight: 0,
      overflow: 'hidden',
      ...(isDialog ? {
        p: 3,
        justifyContent: 'flex-start',
        width: '100%',
        overflow: 'auto'
      } : {})
    }}>
      

      {/* 当没有文件上传时显示说明信息 */}
      {!files.length && (
        <>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: 500,
              lineHeight: 1.8,
              mx: 'auto',
              textAlign: 'center'
            }}
          >
          </Typography>
        </>
      )}

      {/* 文件拖放区域 */}
      <Box 
        {...getRootProps()} 
        {...(requireAuth ? { onClick: handleDropzoneClick } : {})}
        sx={{
          ...dropzoneStyle,
          ...(files.length ? { flexGrow: 0, mb: 3, minHeight: '300px' } : { flexGrow: 1, mb: 4 }),
          ...(isDialog && files.length ? { mt: 3 } : {})
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="body1" fontWeight={500} sx={{ mb: 1, color: theme.palette.primary.main }}>
          {isDragActive
            ? isDragReject
              ? '不支持的文件类型'
              : dropzoneTitle.replace('或点击上传', '')
            : dropzoneTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
          {dropzoneDescription}
        </Typography>
        <Typography
          variant="body2"
          component="a"
          href="http://localhost:3002/guide"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{
            textAlign: 'center',
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            cursor: 'pointer',
            mb: 3
          }}
        >
          点击查看文件上传操作规范
        </Typography>
      </Box>

      {/* 文件列表 */}
      {files.length > 0 && (
        <Box sx={{ mb: 2, flexShrink: 0, maxHeight: '400px', overflowY: 'auto' }}>
          <Typography 
            variant="subtitle2" 
            sx={{ mb: 2, fontWeight: 500, display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}
          >
            <span>已上传文件</span>
            <Chip 
              label={`${files.length}/${MAX_FILES}`} 
              size="small" 
              sx={{ ml: 1, bgcolor: theme.palette.background.light }}
            />
          </Typography>
          <Grid container spacing={2} sx={{ backgroundColor: 'transparent' }}>
            {files.map((file, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.2,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    position: 'relative',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.light
                    }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => removeFile(index)}
                    sx={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'transparent',
                      color: 'rgba(0,0,0,0.4)',
                      padding: '2px',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.05)',
                        color: 'rgba(0,0,0,0.6)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
                    <Box sx={{
                      mr: 1,
                      width: 28,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ '& svg': { fontSize: 28 } }}>
                        {getFileIcon(file.name)}
                      </Box>
                    </Box>
                    <Box sx={{ width: 'calc(100% - 28px)' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          mb: 0.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {truncateFileName(file.name)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* 底部操作区 - 当有文件时显示 */}
      {files.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flexShrink: 0,
          pt: 2,
          gap: 3,
          pb: 4  // 为悬浮按钮留出底部空间
        }}>
          {/* 原子商品选择区域 */}
          {atomicProducts.data.length > 0 && (
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 500,
                  color: theme.palette.primary.main
                }}
              >
                选择翻译服务
              </Typography>
              <RadioGroup
                value={selectedProduct}
                onChange={handleProductChange}
                sx={{ 
                  gap: 1.5,
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap'
                }}
              >
                {atomicProducts.data.filter(product => !product.product_name.includes('加急服务')).map((product) => (
                  <Card 
                    key={product.product_id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      borderRadius: '36px',
                      border: selectedProduct === product.product_id 
                        ? `1px solid ${theme.palette.primary.main}` 
                        : `1px solid ${theme.palette.divider}`,
                      backgroundColor: selectedProduct === product.product_id 
                        ? 'rgba(67, 83, 255, 0.08)' 
                        : '#FFFFFF',
                      '&:hover': {
                        borderColor: theme.palette.primary.light,
                        backgroundColor: selectedProduct === product.product_id 
                          ? 'rgba(67, 83, 255, 0.08)'
                          : 'rgba(67, 83, 255, 0.02)'
                      },
                      flex: '1 1 300px',
                      minWidth: 320,
                      maxWidth: 360,
                      position: 'relative'
                    }}
                    onClick={() => setSelectedProduct(product.product_id)}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute',
                        right: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {product.duration_display}
                    </Typography>
                    
                    <CardContent sx={{ p: '12px 12px 12px 24px', '&:last-child': { pb: '12px' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ flexGrow: 1, pr: '60px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: '0.85rem'
                              }}
                            >
                              {product.product_name}
                            </Typography>
                            <Chip
                              label={product.billing_display}
                              size="small"
                              sx={{ 
                                fontSize: '0.65rem',
                                height: '16px',
                                bgcolor: 'rgba(67, 83, 255, 0.1)',
                                color: theme.palette.primary.main,
                                border: 'none',
                                '& .MuiChip-label': { 
                                  px: 0.5 
                                }
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: '0.7rem'
                            }}
                          >
                            {product.product_description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
              
              {/* 加急服务选项 */}
              {urgentService && (
                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 500,
                      color: theme.palette.primary.main
                    }}
                  >
                    是否加急服务
                  </Typography>
                  <Box sx={{ 
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap'
                  }}>
                    <Card 
                       variant="outlined"
                       sx={{
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         borderRadius: '32px',
                         border: !isUrgent 
                           ? `1px solid ${theme.palette.primary.main}` 
                           : `1px solid ${theme.palette.divider}`,
                         backgroundColor: !isUrgent 
                           ? 'rgba(67, 83, 255, 0.08)' 
                           : '#FFFFFF',
                         '&:hover': {
                           borderColor: theme.palette.primary.light,
                           backgroundColor: !isUrgent 
                             ? 'rgba(67, 83, 255, 0.08)'
                             : 'rgba(67, 83, 255, 0.02)'
                         },
                         minWidth: '140px',
                         flex: '0 0 auto'
                       }}
                      onClick={() => setIsUrgent(false)}
                    >
                      <CardContent sx={{ p: '8px 16px', '&:last-child': { pb: '8px' } }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: theme.palette.primary.main,
                            fontSize: '0.85rem',
                            textAlign: 'center'
                          }}
                        >
                          普通
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.7rem',
                            textAlign: 'center'
                          }}
                        >
                          标准处理时间
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card 
                       variant="outlined"
                       sx={{
                         cursor: 'pointer',
                         transition: 'all 0.2s ease',
                         borderRadius: '32px',
                         border: isUrgent 
                           ? `1px solid ${theme.palette.primary.main}` 
                           : `1px solid ${theme.palette.divider}`,
                         backgroundColor: isUrgent 
                           ? 'rgba(67, 83, 255, 0.08)' 
                           : '#FFFFFF',
                         '&:hover': {
                           borderColor: theme.palette.primary.light,
                           backgroundColor: isUrgent 
                             ? 'rgba(67, 83, 255, 0.08)'
                             : 'rgba(67, 83, 255, 0.02)'
                         },
                         minWidth: '140px',
                         flex: '0 0 auto'
                       }}
                      onClick={() => setIsUrgent(true)}
                    >
                      <CardContent sx={{ p: '8px 16px', '&:last-child': { pb: '8px' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.primary.main,
                              fontSize: '0.85rem',
                              textAlign: 'center'
                            }}
                          >
                            加急
                          </Typography>
                          <Chip
                            label={`额外${urgentService.billing_display}`}
                            size="small"
                            sx={{ 
                              fontSize: '0.65rem',
                              height: '16px',
                              bgcolor: 'rgba(67, 83, 255, 0.1)',
                              color: theme.palette.primary.main,
                              border: 'none',
                              '& .MuiChip-label': { 
                                px: 0.5 
                              }
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.7rem',
                            textAlign: 'center'
                          }}
                        >
                          最快响应，优先认证，最快30分钟
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              )}
              
              {/* 语种选择 */}
              <Box sx={{ mt: 3 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 500,
                    color: theme.palette.primary.main
                  }}
                >
                  选择语种
                </Typography>
                <Box sx={{ 
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}>
                  {availableLanguages.map((lang) => (
                    <Card 
                      key={lang.code}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: '24px',
                        border: language === lang.code 
                          ? `1px solid ${theme.palette.primary.main}` 
                          : `1px solid ${theme.palette.divider}`,
                        backgroundColor: language === lang.code 
                          ? 'rgba(67, 83, 255, 0.08)' 
                          : '#FFFFFF',
                        '&:hover': {
                          borderColor: theme.palette.primary.light,
                          backgroundColor: language === lang.code 
                            ? 'rgba(67, 83, 255, 0.08)'
                            : 'rgba(67, 83, 255, 0.02)'
                        },
                        minWidth: '80px',
                        flex: '0 0 auto'
                      }}
                      onClick={() => setLanguage(lang.code)}
                    >
                      <CardContent sx={{ p: '12px 16px', '&:last-child': { pb: '12px' } }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            fontSize: '0.85rem',
                            textAlign: 'center'
                          }}
                        >
                          {lang.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* 提交按钮 - 仅在有文件时显示 */}
                  {files.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      sx={{ 
                        py: 1.5, 
                        px: 3,
                        height: '48px',
                        borderRadius: '24px',
                        fontWeight: 600,
                        boxShadow: '0 4px 20px rgba(67, 83, 255, 0.3)',
                        minWidth: '120px',
                        flex: '0 0 auto',
                        ml: 'auto',
                        '&:hover': {
                          boxShadow: '0 6px 24px rgba(67, 83, 255, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          transform: 'none',
                          boxShadow: '0 4px 20px rgba(67, 83, 255, 0.2)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      className="webflow-button"
                    >
                      {isSubmitting ? '提交中...' : '提交翻译'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

    </Box>
  );
};

export default TaskUploader;